import strings from 'owa-strings/owa-mail-strings';
import {
    IContextualMenuItem,
    ContextualMenuItemType,
} from 'office-ui-fabric-react/lib/ContextualMenu';
import { listViewStore, shouldSuppressServerMarkReadOnReplyOrForward } from 'owa-mail-list-store';
import { ClientItemId } from 'owa-client-ids';
import { lazyReplyToItem, lazyForwardItem } from 'owa-mail-message-actions';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import { getItemClientId } from 'owa-mail-list-table-operations/lib/actions/store-factory/mapItemTypeToItemRelation';
import Message from 'owa-service/lib/contract/Message';
import doesFolderIdEqualName from 'owa-session-store/lib/utils/doesFolderIdEqualName';
import { lazyMarkItemJunkNotJunkFromReadingPane } from 'owa-mail-triage-action';
import { isJunkEmailEnabledByAdmin } from 'owa-mail-filterable-menu-behavior';
import { lazyMarkItemsAsReadBasedOnNodeIds } from 'owa-mail-mark-read-actions';
import folderStore, { isFolderUnderMsg } from 'owa-folders';

const contextMenuActionSource = 'ContextMenu';

/**
 * Get an array of filterable menu items for mail list context menu
 * @param rowKeys the row keys to show the context menu
 * @param tableViewId the id of the table that contains these rows
 * @return the context menu items
 */
export default function getMailListItemPartContextMenuItems(
    nodeIds: string[],
    tableViewId: string,
    onDismiss: (ev?: any) => void
): IContextualMenuItem[] {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const menuItems: IContextualMenuItem[] = [];

    if (nodeIds.length == 1) {
        const item: Message = getItemToShowFromNodeId(nodeIds[0]);
        const clientItemId = getItemClientId(item, tableView);
        const isInJunkFolder = doesFolderIdEqualName(item.ParentFolderId.Id, 'junkemail');
        const currentFolder = folderStore.folderTable.get(tableView.tableQuery.folderId);
        const isFolderUnderMsgRoot = isFolderUnderMsg(currentFolder);

        if (isFolderUnderMsgRoot) {
            // Reply
            menuItems.push({
                key: 'replyItemPart',
                name: strings.itemHeaderReply,
                title: strings.itemHeaderReply,
                onClick: () => {
                    onReplyReplyAllClicked(clientItemId, tableViewId, false);
                },
            });
        }

        // Reply All
        menuItems.push({
            key: 'replyAllItemPart',
            name: strings.itemHeaderReplyAll,
            title: strings.itemHeaderReplyAll,
            onClick: () => {
                onReplyReplyAllClicked(clientItemId, tableViewId, true);
            },
        });

        // Forward
        menuItems.push({
            key: 'forwardItemPart',
            name: strings.itemHeaderForward,
            title: strings.itemHeaderForward,
            onClick: () => {
                onForwardClicked(clientItemId, tableViewId);
            },
        });
        menuItems.push({
            key: 'ItemPartDivider',
            itemType: ContextualMenuItemType.Divider,
        });
        menuItems.push({
            key: 'markReadUnreadItemPart',
            name: item.IsRead ? strings.itemHeaderMarkAsUnread : strings.itemHeaderMarkAsRead,
            title: item.IsRead ? strings.itemHeaderMarkAsUnread : strings.itemHeaderMarkAsRead,
            onClick: () => {
                onMarkReadUnreadClicked(nodeIds, tableViewId, !item.IsRead);
            },
        });

        if (isFolderUnderMsgRoot && isJunkEmailEnabledByAdmin()) {
            menuItems.push({
                key: 'markJunkNotJunkItemPart',
                name: isInJunkFolder ? strings.markAsNotJunk : strings.markAsJunk,
                title: isInJunkFolder ? strings.markAsNotJunk : strings.markAsJunk,
                onClick: () => {
                    onMarkJunkNotJunkClicked(item.ItemId.Id, tableViewId, !isInJunkFolder);
                },
            });
        }
    } else {
        // Only mark as read/unread supported for multiple item parts
        let hasUnreadItem = false;
        let hasReadItem = false;
        const itemIds: string[] = [];

        for (const nodeId of nodeIds) {
            const item: Message = getItemToShowFromNodeId(nodeId);
            itemIds.push(item.ItemId.Id);

            if (item.IsRead) {
                hasReadItem = true;
            } else {
                hasUnreadItem = true;
            }
        }

        if (hasUnreadItem) {
            menuItems.push({
                key: 'markReadItemParts',
                name: strings.itemHeaderMarkAsRead,
                title: strings.itemHeaderMarkAsRead,
                onClick: () => {
                    onMarkReadUnreadClicked(nodeIds, tableViewId, true);
                },
            });
        }

        if (hasReadItem) {
            menuItems.push({
                key: 'markUnreadItemParts',
                name: strings.itemHeaderMarkAsUnread,
                title: strings.itemHeaderMarkAsUnread,
                onClick: () => {
                    onMarkReadUnreadClicked(nodeIds, tableViewId, false);
                },
            });
        }
    }

    return menuItems;
}

async function onReplyReplyAllClicked(
    clientItemId: ClientItemId,
    tableViewId: string,
    isReplyAll: boolean
): Promise<void> {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyReplyToItem.importAndExecute(
        clientItemId,
        isReplyAll,
        null /* instrumentationContexts */,
        contextMenuActionSource,
        shouldSuppressServerMarkReadOnReplyOrForward(tableView)
    );
}

async function onForwardClicked(clientItemId: ClientItemId, tableViewId: string): Promise<void> {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyForwardItem.importAndExecute(
        clientItemId,
        contextMenuActionSource,
        null /* instrumentationContexts */,
        shouldSuppressServerMarkReadOnReplyOrForward(tableView)
    );
}

async function onMarkReadUnreadClicked(
    nodeIds: string[],
    tableViewId: string,
    shouldMarkAsRead: boolean
): Promise<void> {
    const markItemsAsReadBasedOnNodeIds = await lazyMarkItemsAsReadBasedOnNodeIds.import();
    markItemsAsReadBasedOnNodeIds(
        nodeIds,
        tableViewId,
        shouldMarkAsRead /* isReadValueToSet */,
        true /* isExplicit */,
        contextMenuActionSource
    );
}

async function onMarkJunkNotJunkClicked(
    itemId: string,
    tableViewId: string,
    shouldMarkAsRead: boolean
): Promise<void> {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyMarkItemJunkNotJunkFromReadingPane.importAndExecute(
        itemId,
        tableView,
        contextMenuActionSource,
        null /* instrumentationContexts */
    );
}
